// buildVRCPayload.js
// Helpers para convertir el estado local del wizard VRCPro al payload que espera
// el backend (ver instrucciones/VRC_BACKEND_INTEGRATION_README.md) y armar el
// FormData multipart correspondiente.

// ─── Helpers de conversión de fotos ────────────────────────────────────────

// Convierte una data URL (base64) a Blob. Si recibe algo distinto, lo retorna tal cual.
export async function dataUrlToBlob(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return null;
  if (!dataUrl.startsWith('data:')) return null;
  const res = await fetch(dataUrl);
  return await res.blob();
}

// Toma un array de fotos heterogéneo (data URLs nuevas + objetos {publicId,url}
// existentes en CDN). Devuelve:
//   - keep: array de objetos a preservar en el JSON (los que ya tienen publicId)
//   - newBlobs: array de File para enviar en multipart
async function splitPhotos(arr, fieldPrefix) {
  const keep = [];
  const newBlobs = [];
  if (!Array.isArray(arr)) return { keep, newBlobs };
  let i = 0;
  for (const item of arr) {
    if (item && typeof item === 'object' && item.publicId) {
      keep.push(item);
    } else if (typeof item === 'string' && item.startsWith('data:')) {
      const blob = await dataUrlToBlob(item);
      if (blob) {
        const ext = (blob.type.split('/')[1] || 'jpg').split(';')[0];
        const file = new File([blob], `${fieldPrefix}_${Date.now()}_${i}.${ext}`, { type: blob.type });
        newBlobs.push(file);
      }
    }
    i++;
  }
  return { keep, newBlobs };
}

// ─── Mapper: state local → payload backend ─────────────────────────────────

// Construye el objeto `data` (que va como JSON.stringify en el FormData) y
// la lista de archivos nuevos a subir por bucket (inmueble / c1 / c2 / c3).
//
// `state` debe contener (al menos) todos los useState del wizard VRCPro
// relevantes para el VRC: propiedad, comparables, resultado, textos editables,
// agente, propietario, etc.
export async function buildVRCPayload(state) {
  const {
    // identidad / contexto
    clientId,
    propietario,
    agente,
    // propiedad
    tipoInm, tipo, dir, zona, fecha,
    supCub, supSemi, coefSemi, supTer, coefTer,
    antiguedad, reciclada, dorm, ban, coch, piso, ori,
    estado, estadoCustom, obsf, svc,
    frenteReal, frenteTipo, catProp, amenitiesProp,
    tSup, tFr, tFo, tTopo, tSvc, tSvcCoef, tForma, tHab,
    desc, precioAsp, nota,
    fotosMain,
    // comparables (objetos completos de initComp())
    comp1, comp2, comp3,
    // resultado
    resultado,
    manualMode, manualVRC,
    // textos editables
    foda, analisisComp, recomendacion, contextoMercado, notaFinal,
    criterioProfesional, ctxPrecioZona, ctxCompetencia, ctxAbsorcion,
    // edición: fotos eliminadas (publicIds de fotos que estaban y se quitaron)
    fotosEliminadas,
  } = state;

  // ── 1) propiedadData
  // Helper local para calcular sup. homogeneizada (mismo criterio que calcVRC).
  // - Edificado: supCub + supSemi*coefSemi + supTer*coefTer
  // - Terreno: supTer (lote puro)
  const calcSupHomLocal = (cub, semi, cs, ter, ct, esTer) => {
    if (esTer) return parseFloat(ter) || 0;
    return (parseFloat(cub) || 0)
      + (parseFloat(semi) || 0) * (parseFloat(cs) || 0.5)
      + (parseFloat(ter) || 0) * (parseFloat(ct) || 0.5);
  };
  const esTerreno = tipoInm === 't';
  const supHomProp = esTerreno
    ? (parseFloat(tSup) || 0)
    : calcSupHomLocal(supCub, supSemi, coefSemi, supTer, coefTer, false);

  const propiedadData = {
    // El backend valida `tipo` ∈ ("edificado","terreno"). `tipoInmueble` queda
    // como string descriptivo (Casa, Departamento, Terreno, Lote, etc.).
    tipo: esTerreno ? 'terreno' : 'edificado',
    tipoInmueble: tipo || '',
    direccion: dir || '',
    zona: zona || '',
    fecha: fecha || null,
    // edificado
    supCubierta: parseFloat(supCub) || 0,
    supSemicubierta: parseFloat(supSemi) || 0,
    coefSemicubierta: parseFloat(coefSemi) || 0.5,
    supTerreno: parseFloat(supTer) || 0,
    coefTerreno: parseFloat(coefTer) || 0.5,
    supHomogeneizada: supHomProp,
    antiguedad: parseFloat(antiguedad) || 0,
    reciclada: !!reciclada,
    dormitorios: parseInt(dorm, 10) || 0,
    banos: parseInt(ban, 10) || 0,
    cocheras: parseInt(coch, 10) || 0,
    piso: parseInt(piso, 10) || 0,
    orientacion: parseFloat(ori) || 1,
    estado: parseFloat(estadoCustom || estado) || 0.9,
    obsf: parseFloat(obsf) || 1,
    servicios: svc || '',
    frenteReal: parseFloat(frenteReal) || 0,
    frenteTipo: parseFloat(frenteTipo) || 10,
    categoria: parseFloat(catProp) || 1,
    amenities: Array.isArray(amenitiesProp) ? amenitiesProp : [],
    // terreno (top-level, lo que el backend valida cuando tipo === 'terreno')
    supTotal: parseFloat(tSup) || 0,
    frente: parseFloat(tFr) || 0,
    fondo: parseFloat(tFo) || 0,
    topografia: parseFloat(tTopo) || 1,
    forma: parseFloat(tForma) || 1,
    serviciosCoef: parseFloat(tSvcCoef) || 1,
    habilitacion: parseFloat(tHab) || 1,
    // Conservamos también la estructura anidada por compat con consumidores
    // existentes del front (gráficos, detalle, etc.).
    terreno: {
      superficie: parseFloat(tSup) || 0,
      frente: parseFloat(tFr) || 0,
      fondo: parseFloat(tFo) || 0,
      topografia: parseFloat(tTopo) || 1,
      forma: parseFloat(tForma) || 1,
      servicios: tSvc || '',
      serviciosCoef: parseFloat(tSvcCoef) || 1,
      habilitacion: parseFloat(tHab) || 1,
    },
    descripcion: desc || '',
    nota: nota || '',
  };

  // ── 2) comparables[]
  const mapComp = (c, idx) => {
    const supHomComp = esTerreno
      ? (parseFloat(c?.supTer) || 0)
      : calcSupHomLocal(c?.supCub, c?.supSemi, 0.5, c?.supTer, 0.5, false);
    return {
      index: idx + 1,
      direccion: c.dir || '',
      precioPublicacion: parseFloat(c.precio) || 0,
      supCubierta: parseFloat(c.supCub) || 0,
      supSemicubierta: parseFloat(c.supSemi) || 0,
      supTerreno: parseFloat(c.supTer) || 0,
      supHomogeneizada: supHomComp,
      antiguedad: parseFloat(c.ant) || 0,
      dormitorios: parseInt(c.dorm, 10) || 0,
      banos: parseInt(c.ban, 10) || 0,
      estado: parseFloat(c.estadoCustom || c.estado) || 0.9,
      antCoef: parseFloat(c.antCoefCustom || c.antCoef) || 0.7,
      negociacion: parseFloat(c.negCustom || c.neg) || 0.94,
      tiempo: parseFloat(c.tiempo) || 1,
      ubicacion: parseFloat(c.ubic) || 1,
      categoria: parseFloat(c.cat) || 1,
      acceso: parseFloat(c.accesoCustom || c.acceso) || 1,
      amenities: Array.isArray(c.amenities) ? c.amenities : [],
      // Coeficientes terreno por comparable (usados en el cálculo de ratioLote).
      tTopo: parseFloat(c.tTopo) || 1,
      tForma: parseFloat(c.tForma) || 1,
      tSvcCoef: parseFloat(c.tSvcCoef) || 1,
      tHab: parseFloat(c.tHab) || 1,
      tSvc: c.tSvc || '',
      link: c.link || '',
      venta: c.venta || 'En venta',
      fechaPub: c.fechaPub || null,
      obs: c.obs || '',
    };
  };

  const comparables = [mapComp(comp1, 0), mapComp(comp2, 1)];
  // c3 es opcional; sólo lo incluimos si tiene precio
  if (comp3 && parseFloat(comp3.precio) > 0) {
    comparables.push(mapComp(comp3, 2));
  }

  // ── 3) resultados (recalculado por el front)
  const resultados = resultado ? {
    vrcFinal: resultado.vrc || 0,
    rangoMin: resultado.rangoMin || 0,
    rangoMax: resultado.rangoMax || 0,
    vuPromedio: resultado.vuPromedio || 0,
    supHomProp: resultado.supHomProp || 0,
    baseMercado: resultado.baseMercado || 0,
    ajusteRaw: resultado.ajusteRaw || 1,
    zonaLabel: resultado.zonaLabel || '',
  } : null;

  // ── 4) FOTOS: separar las que ya están en CDN (keep) de las nuevas (Blob)
  const inm = await splitPhotos(fotosMain, 'inm');
  const c1ph = await splitPhotos(comp1?.fotos, 'c1');
  const c2ph = await splitPhotos(comp2?.fotos, 'c2');
  const c3ph = comp3 ? await splitPhotos(comp3.fotos, 'c3') : { keep: [], newBlobs: [] };

  // ── 5) data top-level
  const data = {
    // Backend espera número o null. '' → null para mantener el contrato limpio.
    clientId: clientId !== '' && clientId != null ? (Number(clientId) || null) : null,
    propietario: propietario || '',
    agente: agente || '',
    propiedadData,
    comparables,
    resultados,
    foda: foda || { f: '', o: '', d: '', a: '' },
    analisis: {
      comparativo: analisisComp || '',
      recomendacion: recomendacion || '',
      contextoMercado: contextoMercado || '',
      notaFinal: notaFinal || '',
      criterioProfesional: criterioProfesional || '',
      ctxPrecioZona: ctxPrecioZona || '',
      ctxCompetencia: ctxCompetencia || '',
      ctxAbsorcion: ctxAbsorcion || '',
    },
    precioAspiracional: parseFloat(precioAsp) || null,
    manualVRC: manualMode ? (parseFloat(manualVRC) || null) : null,
    // Fotos existentes (preservadas). El backend espera estos campos top-level.
    fotosInmueble: inm.keep,
    fotosComparables: {
      c1: c1ph.keep,
      c2: c2ph.keep,
      c3: c3ph.keep,
    },
  };

  // En modo edición, indicarle al backend qué fotos borrar.
  if (Array.isArray(fotosEliminadas) && fotosEliminadas.length > 0) {
    data.fotosEliminadas = fotosEliminadas;
  }

  return {
    data,
    files: {
      fotos_inmueble: inm.newBlobs,
      fotos_c1: c1ph.newBlobs,
      fotos_c2: c2ph.newBlobs,
      fotos_c3: c3ph.newBlobs,
    },
  };
}

// Arma un FormData con los campos que espera el backend.
export function buildVRCFormData(payload, extra = {}) {
  const fd = new FormData();
  fd.append('data', JSON.stringify({ ...payload.data, ...extra }));
  for (const [field, blobs] of Object.entries(payload.files)) {
    for (const blob of blobs) fd.append(field, blob);
  }
  return fd;
}
